import React, { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import {
  addBranchGroupZone,
  addUserGroupMember,
  createBranchGroup,
  createUserGroup,
  deleteBranchGroup,
  deleteUserGroup,
  getBranchGroup,
  getUserGroup,
  listBranchGroups,
  listUserGroups,
  removeBranchGroupGrant,
  removeBranchGroupZone,
  removeUserGroupMember,
  setBranchGroupGrant,
  type BranchGroupSummary,
  type UserGroupSummary,
  addBranchGroupAdmin,
  removeBranchGroupAdmin,
} from '@/api/adminGroups';
import { listAdminUsers } from '@/api/adminUsers';
import { createLegalEntity, deleteLegalEntity, legalEntitiesKey, listLegalEntities, updateLegalEntity } from '@/api/legalEntities';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { listZones, type ZoneDto } from '@/api/zones';
import { extractApiError } from '@/utils/apiError';

// VISITS-1 (#175): group mechanism admin UI. A UserGroup grants a role
// (via a BranchGroupGrant) on every zone in a BranchGroup, to every member
// of the UserGroup — instantly, both ways (see the API docstrings). There
// is no "effective access" screen here by design (Alexey's decision) —
// the per-user access page (UserAdminPage) annotates group-derived rows
// with "inherited from group" + a lock instead.

const ZONE_ROLES = ['zone_employee', 'zone_manager', 'zone_admin', 'line_worker', 'chain_office'];

export default function GroupsAdminPage() {
  const { t } = useT();
  usePageTitle(t('admin.groups.title'), t('admin.groups.subtitle'));

  return (
    <div className="container mx-auto max-w-4xl space-y-4 px-4 py-6">
      <UserGroupsCard />
      <BranchGroupsCard />
    </div>
  );
}

// ── User groups ──────────────────────────────────────────────────────────

function UserGroupsCard() {
  const { t } = useT();
  const { data, error, isLoading, mutate } = useSWR('admin-user-groups', () =>
    listUserGroups().then(r => r.data.results),
  );
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  async function handleCreate() {
    const name = window.prompt(t('admin.groups.newUserGroupPrompt'));
    if (!name?.trim()) return;
    setCreating(true);
    try {
      await createUserGroup(name.trim());
      mutate();
    } catch (err) {
      window.alert(extractApiError(err, t('error.general')));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle>{t('admin.groups.userGroupsTitle')}</CardTitle>
          <CardDescription>{t('admin.groups.userGroupsDescription')}</CardDescription>
        </div>
        <Button type="button" size="sm" className="gap-1.5" disabled={creating} onClick={handleCreate}>
          <Plus className="size-3.5" /> {t('admin.groups.newUserGroup')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}
        {isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        )}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t('admin.groups.noUserGroups')}</p>
        )}
        {(data ?? []).map((g: UserGroupSummary) => (
          <UserGroupRow
            key={g.id}
            summary={g}
            expanded={expanded === g.id}
            onToggle={() => setExpanded(expanded === g.id ? null : g.id)}
            onListChanged={mutate}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function UserGroupRow({
  summary, expanded, onToggle, onListChanged,
}: {
  summary: UserGroupSummary;
  expanded: boolean;
  onToggle: () => void;
  onListChanged: () => void;
}) {
  const { t } = useT();
  const { data: detail, mutate } = useSWR(
    expanded ? `admin-user-group:${summary.id}` : null,
    () => getUserGroup(summary.id).then(r => r.data),
  );

  const { data: usersResp } = useSWR(
    expanded ? 'admin-users-for-groups' : null,
    () => listAdminUsers({ limit: 200 }).then(r => r.data.results),
  );

  const memberIds = new Set((detail?.members ?? []).map(m => m.user_id));
  const userOptions: ComboboxOption[] = (usersResp ?? [])
    .filter(u => !memberIds.has(u.id))
    .map(u => ({ value: String(u.id), label: u.username }));

  const [pickedUser, setPickedUser] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleAddMember() {
    if (!pickedUser) return;
    setErr(null);
    setBusy(true);
    try {
      await addUserGroupMember(summary.id, Number(pickedUser));
      setPickedUser('');
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveMember(userId: number) {
    setErr(null);
    setBusy(true);
    try {
      await removeUserGroupMember(summary.id, userId);
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteGroup() {
    if (!window.confirm(t('admin.groups.deleteUserGroupConfirm', { name: summary.name }))) return;
    setBusy(true);
    try {
      await deleteUserGroup(summary.id);
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
      >
        <span className="flex items-center gap-1.5 font-medium">
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {summary.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('admin.groups.memberCount', { n: summary.member_count })}
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 border-t px-3 py-3">
          {!detail && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" /> {t('common.loading')}
            </p>
          )}
          {detail && (
            <>
              <div className="space-y-1">
                {detail.members.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('admin.groups.noMembers')}</p>
                )}
                {detail.members.map(m => (
                  <div key={m.user_id} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1 text-xs">
                    <span>{m.username}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() => handleRemoveMember(m.user_id)}
                      aria-label={t('admin.groups.removeMember')}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
              </div>

              {detail.grants.length > 0 && (
                <div className="space-y-1 border-t pt-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('admin.groups.grantsOnThisGroup')}
                  </div>
                  {detail.grants.map(g => (
                    <div key={g.grant_id} className="text-xs text-muted-foreground">
                      {g.branch_group_name} — {t(`role.${g.role}`)}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t pt-2">
                <div className="min-w-[10rem] flex-1">
                  <Combobox
                    value={pickedUser}
                    onChange={setPickedUser}
                    options={userOptions}
                    placeholder={t('admin.groups.pickUser')}
                    disabled={busy || userOptions.length === 0}
                  />
                </div>
                <Button type="button" size="sm" className="gap-1.5" disabled={!pickedUser || busy} onClick={handleAddMember}>
                  <Plus className="size-3.5" /> {t('admin.groups.addMember')}
                </Button>
              </div>

              <div className="flex justify-end border-t pt-2">
                <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" disabled={busy} onClick={handleDeleteGroup}>
                  <Trash2 className="size-3.5" /> {t('admin.groups.deleteGroup')}
                </Button>
              </div>

              {err && <p className="text-xs text-destructive">{err}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Branch groups ────────────────────────────────────────────────────────

function BranchGroupsCard() {
  const { t } = useT();
  const { data, error, isLoading, mutate } = useSWR('admin-branch-groups', () =>
    listBranchGroups().then(r => r.data.results),
  );
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  async function handleCreate() {
    const name = window.prompt(t('admin.groups.newBranchGroupPrompt'));
    if (!name?.trim()) return;
    setCreating(true);
    try {
      await createBranchGroup(name.trim());
      mutate();
    } catch (err) {
      window.alert(extractApiError(err, t('error.general')));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle>{t('admin.groups.branchGroupsTitle')}</CardTitle>
          <CardDescription>{t('admin.groups.branchGroupsDescription')}</CardDescription>
        </div>
        <Button type="button" size="sm" className="gap-1.5" disabled={creating} onClick={handleCreate}>
          <Plus className="size-3.5" /> {t('admin.groups.newBranchGroup')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}
        {isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        )}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t('admin.groups.noBranchGroups')}</p>
        )}
        {(data ?? []).map((g: BranchGroupSummary) => (
          <BranchGroupRow
            key={g.id}
            summary={g}
            expanded={expanded === g.id}
            onToggle={() => setExpanded(expanded === g.id ? null : g.id)}
            onListChanged={mutate}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function BranchGroupRow({
  summary, expanded, onToggle, onListChanged,
}: {
  summary: BranchGroupSummary;
  expanded: boolean;
  onToggle: () => void;
  onListChanged: () => void;
}) {
  const { t } = useT();
  const { data: detail, mutate } = useSWR(
    expanded ? `admin-branch-group:${summary.id}` : null,
    () => getBranchGroup(summary.id).then(r => r.data),
  );
  const { data: allZonesResp } = useSWR(expanded ? 'admin-zones-for-groups' : null, () =>
    listZones(1000).then(r => r.data.results),
  );
  const { data: userGroupsResp } = useSWR(expanded ? 'admin-user-groups' : null, () =>
    listUserGroups().then(r => r.data.results),
  );

  const zoneIds = new Set((detail?.zones ?? []).map(z => z.zone_id));
  const zoneOptions: ComboboxOption[] = (allZonesResp ?? [])
    .filter((z: ZoneDto) => !zoneIds.has(Number(z.id)))
    .map((z: ZoneDto) => ({ value: String(z.id), label: z.humanreadable_name || `#${z.id}` }));

  const grantedGroupIds = new Set((detail?.grants ?? []).map(g => g.user_group_id));
  const userGroupOptions = (userGroupsResp ?? []).filter(g => !grantedGroupIds.has(g.id));

  const [pickedZone, setPickedZone] = useState('');
  const [pickedUserGroup, setPickedUserGroup] = useState('');
  const [pickedRole, setPickedRole] = useState('zone_employee');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleAddZone() {
    if (!pickedZone) return;
    setErr(null);
    setBusy(true);
    try {
      await addBranchGroupZone(summary.id, Number(pickedZone));
      setPickedZone('');
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveZone(zoneId: number) {
    setErr(null);
    setBusy(true);
    try {
      await removeBranchGroupZone(summary.id, zoneId);
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleAddGrant() {
    if (!pickedUserGroup) return;
    setErr(null);
    setBusy(true);
    try {
      await setBranchGroupGrant(summary.id, Number(pickedUserGroup), pickedRole);
      setPickedUserGroup('');
      mutate();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveGrant(grantId: number) {
    setErr(null);
    setBusy(true);
    try {
      await removeBranchGroupGrant(summary.id, grantId);
      mutate();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteGroup() {
    if (!window.confirm(t('admin.groups.deleteBranchGroupConfirm', { name: summary.name }))) return;
    setBusy(true);
    try {
      await deleteBranchGroup(summary.id);
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
      >
        <span className="flex items-center gap-1.5 font-medium">
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {summary.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('admin.groups.zoneCount', { n: summary.zone_count })}
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 border-t px-3 py-3">
          {!detail && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" /> {t('common.loading')}
            </p>
          )}
          {detail && (
            <>
              <div className="space-y-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('admin.groups.branchesInGroup')}
                </div>
                {detail.zones.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('admin.groups.noBranchesInGroup')}</p>
                )}
                {detail.zones.map(z => (
                  <div key={z.zone_id} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1 text-xs">
                    <span>{z.zone_name}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() => handleRemoveZone(z.zone_id)}
                      aria-label={t('admin.groups.removeBranch')}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <div className="min-w-[10rem] flex-1">
                    <Combobox
                      value={pickedZone}
                      onChange={setPickedZone}
                      options={zoneOptions}
                      placeholder={t('admin.groups.pickBranch')}
                      disabled={busy || zoneOptions.length === 0}
                    />
                  </div>
                  <Button type="button" size="sm" className="gap-1.5" disabled={!pickedZone || busy} onClick={handleAddZone}>
                    <Plus className="size-3.5" /> {t('admin.groups.addBranch')}
                  </Button>
                </div>
              </div>

              <div className="space-y-1 border-t pt-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('admin.groups.grantsTitle')}
                </div>
                {detail.grants.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('admin.groups.noGrants')}</p>
                )}
                {detail.grants.map(g => (
                  <div key={g.grant_id} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1 text-xs">
                    <span>{g.user_group_name} — {t(`role.${g.role}`)}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={busy}
                      onClick={() => handleRemoveGrant(g.grant_id)}
                      aria-label={t('admin.groups.removeGrant')}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <div className="min-w-[10rem] flex-1">
                    <Combobox
                      value={pickedUserGroup}
                      onChange={setPickedUserGroup}
                      options={userGroupOptions.map(g => ({ value: String(g.id), label: g.name }))}
                      placeholder={t('admin.groups.pickUserGroup')}
                      disabled={busy || userGroupOptions.length === 0}
                    />
                  </div>
                  <div className="w-40">
                    <Select value={pickedRole} onChange={e => setPickedRole(e.target.value)} disabled={busy}>
                      {ZONE_ROLES.map(r => (
                        <option key={r} value={r}>{t(`role.${r}`)}</option>
                      ))}
                    </Select>
                  </div>
                  <Button type="button" size="sm" className="gap-1.5" disabled={!pickedUserGroup || busy} onClick={handleAddGrant}>
                    <Plus className="size-3.5" /> {t('admin.groups.addGrant')}
                  </Button>
                </div>
              </div>

              <BranchGroupAdmins groupId={summary.id} admins={detail.admins ?? []} onChanged={() => { void mutate(); onListChanged(); }} />

              <BranchGroupLegalEntities groupId={summary.id} zones={detail.zones} />

              <div className="flex justify-end border-t pt-2">
                <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" disabled={busy} onClick={handleDeleteGroup}>
                  <Trash2 className="size-3.5" /> {t('admin.groups.deleteGroup')}
                </Button>
              </div>

              {err && <p className="text-xs text-destructive">{err}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Администраторы группы филиалов (Алексей 22.10, #1969): админ видит и настраивает все
// филиалы группы (роль zone_admin через служебную группу «<группа> · админы»).
function BranchGroupAdmins({ groupId, admins, onChanged }: {
  groupId: number;
  admins: { user_id: number; username: string }[];
  onChanged: () => void;
}) {
  const { t } = useT();
  const { data: users } = useSWR('admin-users-for-groups', () => listAdminUsers({ limit: 200 }).then(r => r.data.results));
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const adminIds = new Set(admins.map(a => a.user_id));
  const options: ComboboxOption[] = (users ?? []).filter(u => !adminIds.has(u.id)).map(u => ({ value: String(u.id), label: u.username }));
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); } catch (e) { setErr(extractApiError(e, t('error.general'))); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 border-t pt-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Администраторы группы</div>
      <p className="text-xs text-muted-foreground">Видят и настраивают все филиалы группы, в том числе добавленные позже.</p>
      {admins.length === 0 && <p className="text-xs text-muted-foreground">Пока нет.</p>}
      {admins.map(a => (
        <div key={a.user_id} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1 text-xs">
          <span>{a.username}</span>
          <Button variant="ghost" size="icon-sm" disabled={busy} onClick={() => run(() => removeBranchGroupAdmin(groupId, a.user_id))} aria-label="Убрать">
            <X className="size-3" />
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[10rem] flex-1">
          <Combobox value={picked} onChange={setPicked} options={options} placeholder={t('admin.groups.pickUser')} disabled={busy} />
        </div>
        <Button type="button" size="sm" className="gap-1.5" disabled={!picked || busy}
          onClick={() => run(async () => { await addBranchGroupAdmin(groupId, Number(picked)); setPicked(''); })}>
          <Plus className="size-3.5" /> Сделать админом
        </Button>
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

// Юрлица группы филиалов (Алексей 23.10, #2005/#2010): первична группа, ЮЛ — справочник внутри
// неё; доступов не даёт. На ЮЛ вешается подключение «Чеков».
function BranchGroupLegalEntities({ groupId, zones }: {
  groupId: number;
  zones: { zone_id: number; zone_name: string }[];
}) {
  const { t } = useT();
  const { data, mutate } = useSWR(legalEntitiesKey(groupId), () => listLegalEntities(groupId).then(r => r.data.legal_entities));
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [inn, setInn] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await mutate(); } catch (e) { setErr(extractApiError(e, t('error.general'))); } finally { setBusy(false); }
  };
  const toggle = (set: Set<number>, id: number) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };
  return (
    <div className="space-y-1.5 border-t pt-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Юрлица</div>
      <p className="text-xs text-muted-foreground">Юрлица владельца и их филиалы. Доступы не меняют — права от группы; к юрлицу подключаются «Чеки».</p>
      {(data ?? []).length === 0 && !adding && <p className="text-xs text-muted-foreground">Пока нет.</p>}
      {(data ?? []).map(le => (
        <div key={le.id} className="space-y-1 rounded-md bg-muted/30 px-2 py-1.5 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{le.name}{le.inn && <span className="font-normal text-muted-foreground"> · ИНН {le.inn}</span>}</span>
            <span className="flex items-center gap-2">
              {le.connections.length > 0 && <span className="text-muted-foreground">Чеки подключены</span>}
              <Button variant="ghost" size="icon-sm" disabled={busy} aria-label="Удалить юрлицо"
                onClick={() => { if (window.confirm(`Удалить юрлицо «${le.name}»?`)) void run(() => deleteLegalEntity(le.id)); }}>
                <X className="size-3" />
              </Button>
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {zones.map(z => {
              const on = le.zones.some(x => x.id === z.zone_id);
              return (
                <button key={z.zone_id} type="button" disabled={busy}
                  onClick={() => void run(() => updateLegalEntity(le.id, {
                    zone_ids: on ? le.zones.filter(x => x.id !== z.zone_id).map(x => x.id) : [...le.zones.map(x => x.id), z.zone_id],
                  }))}
                  className={cn('rounded-full border px-2 py-0.5 transition-colors',
                    on ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted')}>
                  {z.zone_name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {adding ? (
        <div className="space-y-2 rounded-md border p-2">
          <div className="flex flex-wrap gap-2">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Название, например ООО «Кофейный Капитал»" className="h-8 min-w-[12rem] flex-1 text-xs" />
            <Input value={inn} onChange={e => setInn(e.target.value)} placeholder="ИНН" className="h-8 w-36 text-xs" />
          </div>
          <div className="flex flex-wrap gap-1 text-xs">
            {zones.map(z => (
              <button key={z.zone_id} type="button" onClick={() => setPicked(p => toggle(p, z.zone_id))}
                className={cn('rounded-full border px-2 py-0.5 transition-colors',
                  picked.has(z.zone_id) ? 'border-primary bg-primary/10' : 'border-border text-muted-foreground hover:bg-muted')}>
                {z.zone_name}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !name.trim()} onClick={() => run(async () => {
              await createLegalEntity({ group: groupId, name: name.trim(), inn: inn.trim(), zone_ids: [...picked] });
              setAdding(false); setName(''); setInn(''); setPicked(new Set());
            })}>Добавить</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Отмена</Button>
          </div>
        </div>
      ) : (
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setAdding(true)}>
          <Plus className="size-3.5" /> Добавить юрлицо
        </Button>
      )}
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}
